import Cookies from 'js-cookie';
import React, { FunctionComponent, useState } from 'react';

import Button from 'components/Button/Button';
import { CookieBarTypes } from 'components/CookieBar/CookieBar';
import Text from 'components/Text/Text';
import Toggle from 'components/Toggle/Toggle';
import { useGlobalStore } from 'store';
import { stringToBoolean } from 'utils';
import { ColorNames, colors } from 'utils/styles/theme';

import { CookiesOverlayVariants } from './CookiesOverlay.animations';

import * as S from './CookiesOverlay.styles';

export interface CookiesOverlayProps {
  necessaryCookieLabel: CookieBarTypes['necessaryCookieLabel'];
  functionalityCookieLabel: CookieBarTypes['functionalityCookieLabel'];
  analyticsCookieLabel: CookieBarTypes['analyticsCookieLabel'];
  acceptCookiesButton: string;
  handleAccept?: () => void;
}

const defaultProps: Partial<CookiesOverlayProps> = {
  necessaryCookieLabel: 'Strictly necessary',
  functionalityCookieLabel: 'Functionality',
  analyticsCookieLabel: 'Analyticts',
};

const CookiesOverlay: FunctionComponent<CookiesOverlayProps> = ({
  necessaryCookieLabel,
  functionalityCookieLabel,
  analyticsCookieLabel,
  acceptCookiesButton,
  handleAccept,
  ...rest
}) => {
  const [functionality, setFunctionality] = useState<boolean>(
    stringToBoolean(Cookies.get('dbp.functionality'))
  );
  const [analytics, setAnalytics] = useState<boolean>(
    stringToBoolean(Cookies.get('dbp.analytics'))
  );

  const { setCookiesConfirmation } = useGlobalStore();

  const acceptCookies = () => {
    Cookies.set('dbp.cookiesConsent', 'true', { expires: 365 });
    Cookies.set('dbp.functionality', functionality.toString(), {
      expires: 365,
    });
    Cookies.set('dbp.analytics', analytics.toString(), { expires: 365 });

    setCookiesConfirmation(true);
    handleAccept();
  };

  const settingsComp = (
    <S.SettingsWrapper
      initial="initial"
      animate="visible"
      exit="exit"
      variants={CookiesOverlayVariants.settings}
    >
      <S.SettingRow opacity={0.3}>
        <Text
          size="small"
          text={necessaryCookieLabel}
          color={ColorNames.lighterMineShaft}
        />
        <Toggle disabled color={colors.lighterMineShaft} initialState />
      </S.SettingRow>

      <S.SettingRow onClick={() => setFunctionality(prev => !prev)}>
        <Text
          size="small"
          text={functionalityCookieLabel}
          color={ColorNames.lighterMineShaft}
        />
        <Toggle
          color={functionality ? colors.black : colors.codGrayLight + 'B5'}
          isActive={functionality}
        />
      </S.SettingRow>

      <S.SettingRow onClick={() => setAnalytics(prev => !prev)}>
        <Text
          size="small"
          text={analyticsCookieLabel}
          color={ColorNames.lighterMineShaft}
        />
        <Toggle
          color={analytics ? colors.black : colors.codGrayLight + 'B5'}
          isActive={analytics}
        />
      </S.SettingRow>
    </S.SettingsWrapper>
  );

  return (
    <S.Wrapper {...rest}>
      <S.Container>
        {settingsComp}

        <Button label={acceptCookiesButton} onClick={acceptCookies} />
      </S.Container>
    </S.Wrapper>
  );
};

CookiesOverlay.defaultProps = defaultProps;

export default CookiesOverlay;
